import React, { useState, useRef, useEffect } from "react";
import { Draggable } from "react-beautiful-dnd";
import styled from "styled-components";
import TaskMenu from "./TaskMenu";
import Subtasks from "./SubTask";
import parse from "html-react-parser";

const TaskCard = ({
  item,
  index,
  onMoveTask,
  onSubtaskComplete,
  onUpdate,
  onSubtaskDelete,
  columnId,
  onSubtaskReorder,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const menuRef = useRef(null);
  const moreButtonRef = useRef(null);

  // Helper function to determine if task should be greyed out
  const isTaskGreyedOut = (task) => {
    // Check if task is in MIT column but has achieved status
    if (
      task.id.startsWith("mit-achieved-") ||
      (task.id.startsWith("mit-") && task.status === "Achieved")
    ) {
      return true;
    }
    // Check if task is in DO column but has done status
    if (
      task.id.startsWith("do-done-") ||
      (task.id.startsWith("do-") && task.status === "Done")
    ) {
      return true;
    }
    return false;
  };

  const isGreyedOut = isTaskGreyedOut(item);

  // Helper function to check if task is in a completed column
  const isInCompletedColumn = () => {
    return (
      columnId === "completedList" ||
      item.id.includes("-done-") ||
      item.id.includes("-achieved-") ||
      item.status === "Done" ||
      item.status === "Achieved"
    );
  };

  // Helper function to check if task has subtasks
  const hasSubtasks = () => {
    return item.sub_tasks && item.sub_tasks.length > 0;
  };

  // Helper function to check if subtasks should be shown
  const shouldShowSubtasks = () => {
    if (!hasSubtasks()) return false;

    // Always show subtasks in non-completed columns
    if (!isInCompletedColumn()) return true;

    // In completed columns, only show when expanded
    return isExpanded;
  };

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleExpandToggle = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showMenu &&
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        moreButtonRef.current &&
        !moreButtonRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showMenu]);

  const getCategoryClass = (category) => {
    switch (category) {
      case "Urgent  Important": // two spaces
        return "urgent-important"; // red
      case "Urgent  Not Important": // two spaces
        return "urgent-not-important"; // orange
      case "Important  Not Urgent": // two spaces
        return "important-not-urgent"; // yellow
      case "Not Important  Not Urgent": // two spaces
        return "not-important-not-urgent"; // gray
      default:
        return "not-important-not-urgent"; // gray as fallback
    }
  };

  const hasContent = (htmlString) => {
    if (!htmlString || typeof htmlString !== "string") return false;

    const textContent = htmlString.replace(/<[^>]*>/g, "").trim();
    const hasMediaTags = /<(img|video|audio|iframe|embed)[^>]*>/i.test(
      htmlString,
    );

    return textContent !== "" || hasMediaTags;
  };

  const getCategoryIndicatorClass = (category) => {
    switch (category) {
      case "Urgent  Important": // two spaces
        return "red"; // red
      case "Urgent  Not Important": // two spaces
        return "orange"; // orange
      case "Important  Not Urgent": // two spaces
        return "yellow"; // yellow
      case "Not Important  Not Urgent": // two spaces
        return "gray"; // gray
      default:
        return "gray"; // gray as fallback
    }
  };

  return (
    <Draggable draggableId={item.id} index={index} isDragDisabled={isGreyedOut}>
      {(provided, snapshot) => (
        <CardContainer
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          isDragging={snapshot.isDragging}
          isGreyedOut={isGreyedOut}
        >
          <div
            className={`kanban-task-indicator ${getCategoryIndicatorClass(
              item.category,
            )}`}
          ></div>

          {showMenu && (
            <div ref={menuRef}>
              <TaskMenu
                onClose={() => setShowMenu(false)}
                onMove={(destination) => onMoveTask(item.id, destination)}
                taskId={item.id}
                onUpdate={onUpdate}
                taskData={{
                  title: item.content, // Main task title/description
                  description: item.description, // Rich text content
                  level: item.level, // Current level
                  priority: item.priority, // Current priority
                  due_date: item.due_date, // Due date in YYYY-MM-DD format
                  sub_tasks: item.sub_tasks, // Array of subtasks
                  is_reoccuring: item.is_reoccuring, // Recurring flag
                  recurring_task: item.recurring_task, // Recurring task configuration
                }}
              />
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center">
            <span className="text-body-b4" style={{ color: "#c8ced3" }}>
              {item.source_type ?? "Source Type"}
            </span>
            <div className="d-flex gap-2 align-items-center">
              {item.done && (
                <img src="/icons/calender-circle-tick.svg" alt="Done" />
              )}
              {item.is_reoccuring && (
                <img src="/icons/refresh-icon.svg" alt="Re occuring" />
              )}
              <img
                ref={moreButtonRef}
                className={`task-more-button ${isGreyedOut ? "disabled" : ""}`}
                src="/icons/task-management-more.svg"
                alt="More options"
                onClick={(e) => {
                  if (isGreyedOut) {
                    e.stopPropagation();
                    return;
                  }
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                style={{
                  cursor: isGreyedOut ? "not-allowed" : "pointer",
                  opacity: isGreyedOut ? 0.3 : 1,
                }}
              />
            </div>
          </div>

          <h3 className="mb-0 kanban-task-details">
            {parse(item.content)}

            <br />
            <div className="task-notes-2">
              {hasContent(item.description) ? parse(item.description) : null}
            </div>
          </h3>

          {item.start_time && item.end_time && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "6px",
                color: "#c8ced3",
                marginTop: "6px",
                marginBottom: "8px",
                lineHeight: "1.4",
              }}
              className="text-body-b4"
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span>{item.start_time}</span>
                <span>-</span>
                <span>{item.end_time}</span>
              </div>

              <div
                style={{
                  whiteSpace: "nowrap",
                  opacity: 0.9,
                  fontSize: "13px",
                }}
              >
                ({item.time_duration})
              </div>
            </div>
          )}

          <div className="d-flex justify-content-between mt-2 gap-1">
            <div className="d-flex gap-3">
              <div className="kanban-task-date-badge light-body-subheader">
                {item.date}
              </div>

              <div
                className={`kanban-task-header-badge ${getCategoryClass(
                  item.category,
                )} light-body-subheader`}
                style={{ color: "#c8ced3", fontSize: " 15px" }}
              >
                {item.category}
              </div>
            </div>
            {item.dataCount > 0 && (
              <div
                className="d-flex"
                style={{ gap: "3px" }}
                onClick={
                  isInCompletedColumn() && hasSubtasks()
                    ? handleExpandToggle
                    : undefined
                }
                title={
                  isInCompletedColumn() && hasSubtasks()
                    ? isExpanded
                      ? "Collapse subtasks"
                      : "Expand subtasks"
                    : undefined
                }
              >
                <img
                  style={{ width: "16px", height: "16px" }}
                  src="/icons/task-management-data-2.svg"
                  alt="Data"
                />
                <div className="kanban-data-count text-small-s2">
                  {item.dataCount}
                </div>
              </div>
            )}
          </div>

          {shouldShowSubtasks() && (
            <Subtasks
              subTasks={item.sub_tasks}
              parentStatus={item.category}
              onSubtaskComplete={isGreyedOut ? () => {} : onSubtaskComplete}
              onSubtaskDelete={isGreyedOut ? () => {} : onSubtaskDelete}
              taskId={
                item.id.includes("only-")
                  ? item.id.split("only-")[1]
                  : item.id.split("-")[1]
              } // Extract actual task ID from formatted ID
              onSubtaskReorder={onSubtaskReorder}
              isDisabled={isGreyedOut}
              isParentCompleted={isInCompletedColumn()}
            />
          )}

          <style jsx>{`
            .drag-handle {
              position: absolute;
              top: 0;
              left: 0;
              bottom: 0;
              width: 20px;
              cursor: grab;
            }
            .drag-handle:active {
              cursor: grabbing;
            }
          `}</style>
        </CardContainer>
      )}
    </Draggable>
  );
};

const CardContainer = styled.div`
  background: var(--Bg-Border, #1b2c3a);
  border-radius: 8px;
  padding: 16px;
  position: relative;
  cursor: ${(props) => (props.isGreyedOut ? "not-allowed" : "grab")};
  margin-bottom: 12px;
  border: 1px solid transparent;
  border-color: ${(props) => (props.isDragging ? "#5a9bff" : "transparent")};
  box-shadow: ${(props) =>
    props.isDragging ? "0 4px 8px rgba(90, 155, 255, 0.2)" : "none"};

  /* Greyed out styling */
  ${(props) =>
    props.isGreyedOut &&
    `
    opacity: 0.5;
    background: #0f1a22;
    border-color: rgba(255, 255, 255, 0.1);
    
    .kanban-task-details {
      color: #8a9ba8 !important;
    }
    
    .kanban-task-date-badge {
      background: rgba(255, 255, 255, 0.05) !important;
      color: #8a9ba8 !important;
    }
    
    .kanban-task-header-badge {
      opacity: 0.6;
    }
    
    .kanban-data-count {
      color: #8a9ba8 !important;
    }
    
    .text-body-b4 {
      color: #8a9ba8 !important;
    }
    
    .task-more-button {
      cursor: not-allowed !important;
      opacity: 0.3 !important;
    }
  `}

  &:active {
    cursor: grabbing;
  }

  .kanban-task-indicator {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-radius: 8px 0 0 8px;

    &.red {
      background: #dc3545; /* red for Urgent Important */
    }
    &.orange {
      background: #fd7e14; /* orange for Urgent Not Important */
    }
    &.yellow {
      background: #ffc107; /* yellow for Important Not Urgent */
    }
    &.gray {
      background: #6c757d; /* gray for Not Important Not Urgent */
    }
  }

  .kanban-task-details {
    color: white;
    margin: 8px 0;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .kanban-task-details * {
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .kanban-task-details ul,
  .kanban-task-details ol {
    margin-bottom: 0;
  }

  .kanban-task-date-badge {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    padding: 4px 8px;
    color: white;
  }

  .kanban-task-header-badge {
    border-radius: 4px;
    padding: 4px 8px;

    &.hit {
      background: rgba(255, 90, 90, 0.2);
      color: #ff5a5a;
    }
    &.mit {
      background: rgba(90, 155, 255, 0.2);
      color: #5a9bff;
    }
    &.do {
      background: rgba(255, 222, 90, 0.2);
      color: #ffde5a;
    }
  }

  .kanban-data-count {
    color: white;
  }

  .task-more-button {
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.1);
    }
  }

  touch-action: none; /* Prevent touch scrolling while dragging */
  user-select: none; /* Prevent text selection during drag */
  z-index: ${(props) => (props.isDragging ? 1000 : "auto")};
  transform: ${(props) => (props.isDragging ? "rotate(2deg)" : "none")};

  // Add these to ensure proper spacing
  margin-bottom: 0; // Remove the margin-bottom you had
  width: 100%;
  box-sizing: border-box; /* Include padding in width calculation */

  /* Add transition for smooth movement */
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;

  ${(props) =>
    props.isDragging &&
    `
    transform: rotate(2deg);
    opacity: 0.8;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  `}
`;

export default TaskCard;
